import { renderFormula } from "@/lib/chemicalFormula";
import { useParams, Link } from "react-router-dom";
import { getStepDisplayName } from "@/lib/stepUtils";
import { useProjects } from "@/hooks/useProjects";
import { AppHeader } from "@/components/AppHeader";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Chemical } from "@/types/chemistry";

const BillOfMaterials = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { getProject } = useProjects();
  const project = getProject(projectId!);

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="flex items-center justify-center py-20">
          <p className="text-muted-foreground">Compound not found</p>
        </div>
      </div>
    );
  }

  // Aggregate all chemicals
  const allMaterials: (Chemical & { source: string; type: string })[] = [];
  project.steps.forEach((step) => {
    const displayName = getStepDisplayName(step);
    step.reactants.forEach((c) => allMaterials.push({ ...c, source: displayName, type: "Reactant" }));
    step.reagents.forEach((c) => allMaterials.push({ ...c, source: displayName, type: "Reagent" }));
    step.products.forEach((c) => allMaterials.push({ ...c, source: displayName, type: "Product" }));
  });

  const filledMaterials = allMaterials.filter((m) => m.formula.trim());
  const totalCost = filledMaterials.reduce((sum, m) => sum + (m.price || 0), 0);
  const totalCO2 = filledMaterials.reduce((sum, m) => sum + (m.co2eq || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-5xl px-6 py-6">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Synthesis Plans</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href={`/project/${project.id}`}>{project.name}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Bill of Materials</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <h1 className="text-2xl font-bold tracking-tight mb-6">Bill of Materials</h1>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Total Materials</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{filledMaterials.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Est. Total Cost</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">€{totalCost.toFixed(2)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Total CO₂eq</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{totalCO2.toFixed(2)} kg</p>
            </CardContent>
          </Card>
        </div>

        {/* Materials table */}
        <div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Step</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs">Formula</TableHead>
                <TableHead className="text-xs">CAS #</TableHead>
                <TableHead className="text-xs">Price (€/mol)</TableHead>
                <TableHead className="text-xs">CO₂eq (kg/mol)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filledMaterials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                    No materials with data yet. Add chemicals to your synthesis steps.
                  </TableCell>
                </TableRow>
              ) : (
                filledMaterials.map((m, i) => (
                  <TableRow key={`${m.id}-${i}`}>
                    <TableCell className="text-sm">{m.source}</TableCell>
                    <TableCell className="text-sm">{m.type}</TableCell>
                    <TableCell className="text-sm">{m.name || "—"}</TableCell>
                    <TableCell className="text-sm font-mono">{renderFormula(m.formula)}</TableCell>
                    <TableCell className="text-sm">{m.casNumber || "—"}</TableCell>
                    <TableCell className="text-sm">{m.price ? `€${m.price.toFixed(2)}` : "—"}</TableCell>
                    <TableCell className="text-sm">{m.co2eq ? `${m.co2eq.toFixed(2)}` : "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
};

export default BillOfMaterials;
